/**
 * Entry point — 360 CONFIGURATOR (separate workflow, served from 360.html).
 *
 * This is its OWN app, intentionally split from the 3D configurator (src/main.ts):
 * editing the 360 here can NEVER affect the 3D configurator, and vice-versa. It boots
 * the same engine + design system, but the pre-rendered 360 flipbook (Spin360) is the
 * primary on-screen viewer; the live 3D stays hidden behind it and exists only to power
 * AR + the true model dimensions. The 3D configurator does not import Spin360 at all.
 *
 * Boots end-to-end:
 *   1. Detect device profile
 *   2. Load product config
 *   3. Decode + validate URL state
 *   4. Create engine (hidden — AR + dimensions only)
 *   5. Bootstrap UI + ALWAYS mount the 360 flipbook
 *   6. Hide loading spinner
 */

import { getDeviceProfile } from "@/perf/GPUTier";
import { loadProductConfig, DEFAULT_STATE } from "@/state/Config";
import { decodeState } from "@/state/URLState";
import { Signal } from "@/state/Store";
import { Engine } from "@/engine/Engine";
import { bootstrapUI } from "@/ui/App";
import { bootstrapMobileUI } from "@/ui/MobileApp";
import { initTracker, trackEvent } from "@/analytics/Tracker";
import { warmDracoDecoder } from "@/engine/ModuleLoader";
import { resetLodInstrumentation, setForceMobileTier } from "@/engine/Lod";
import { createBakedArController } from "@/ui/BakedArController";
import { createArController } from "@/ui/ArController";
import { getHostTheme, applyHostTheme } from "@/theme/hostTheme";
import type { ConfigState, ProductConfig } from "@/types";

/** Mobile UI themes. Pure CSS-token swaps keyed off <html data-nv-theme>. */
type NvTheme = "spatial-dark" | "spatial-light" | "bloc";
const NV_THEMES: readonly NvTheme[] = ["spatial-dark", "spatial-light", "bloc"];
/** Scene-background hex per theme so the opaque WebGL canvas blends with the UI. */
const THEME_VIEWER_BG: Record<NvTheme, string> = {
  "spatial-dark": "#0A0B10",
  "spatial-light": "#F5F1E8",
  bloc: "#FFF8EE",
};

/**
 * Resolve the mobile theme from ?theme=…, set <html data-nv-theme>, and return it.
 * Defaults to spatial-light (the GLB-validated render target). Pure attribute
 * switch — no per-theme JS in the UI, multi-tenant clean.
 */
function resolveTheme(): NvTheme {
  let theme: NvTheme = "spatial-light";
  if (typeof window !== "undefined") {
    const raw = new URLSearchParams(window.location.search).get("theme");
    if (raw && (NV_THEMES as readonly string[]).includes(raw)) theme = raw as NvTheme;
  }
  if (typeof document !== "undefined") {
    document.documentElement.dataset.nvTheme = theme;
  }
  return theme;
}

/** The responsive UI now serves ALL widths (phone column → desktop side-by-side via
 *  CSS), so it's the single UI everywhere. The legacy desktop layout (App.ts) is kept
 *  only behind ?legacy=1 as a rollout safety fallback. */
function shouldUseMobileUI(_profileIsMobile: boolean): boolean {
  if (typeof window !== "undefined" && new URLSearchParams(window.location.search).has("legacy")) return false;
  return true;
}

/** Clamp decoded state to options that actually exist in the product. */
function validateState(state: ConfigState, product: ProductConfig): ConfigState {
  const forme = product.formes.find((f) => f.id === state.formeId)
    ?? product.formes.find((f) => f.id === DEFAULT_STATE.formeId)
    ?? product.formes[0];
  const leg = product.legs.find((l) => l.id === state.legId)
    ?? product.legs.find((l) => l.id === DEFAULT_STATE.legId)
    ?? product.legs[0];
  // Produit a `materials` (Amada) : t = coloris, m = matiere. Sans ce bloc, `fabrics` est vide,
  // t etait efface et la page partait sur le premier coloris (navy) quel que soit le lien
  // partage, y compris pour l'AR (constate le 2026-09-07 : f=12-PCM&t=charcoal chargeait navy).
  const mats = product.materials ?? [];
  if (mats.length > 0) {
    const wantCol = state.colorwayId || state.fabricId;
    const mat = mats.find((m) => m.id === state.materialId)
      ?? mats.find((m) => m.colorways.some((c) => c.id === wantCol))
      ?? mats[0];
    const cw = mat.colorways.find((c) => c.id === wantCol) ?? mat.colorways[0];
    return { ...state, formeId: forme.id, materialId: mat.id, colorwayId: cw?.id, fabricId: cw?.id ?? "", legId: leg?.id ?? "" };
  }
  const fabric = product.fabrics.find((f) => f.id === state.fabricId)
    ?? product.fabrics.find((f) => f.id === DEFAULT_STATE.fabricId)
    ?? product.fabrics[0];
  // forme is guaranteed (formes are mandatory); fabric/leg may be absent on a
  // non-upholstered product (e.g. a lamp) → empty id, which the engine + UI skip.
  return { ...state, formeId: forme.id, fabricId: fabric?.id ?? "", legId: leg?.id ?? "" };
}

async function main() {
  const start = performance.now();
  // Instrumentation marks consumed by the E2E perf harness. Reset on every boot
  // so a refresh in Playwright gives clean numbers.
  performance.clearMarks();
  performance.clearMeasures();
  performance.mark("configurator:start");
  window.__NEOVAA_GLTF_LOADS__ = 0;
  window.__NEOVAA_GLTF_FALLBACKS__ = 0;
  window.__NEOVAA_GLB_ERRORS__ = [];
  window.__NEOVAA_SCENE_READY__ = false;
  resetLodInstrumentation();
  window.__NEOVAA_RESET_PERF__ = () => {
    performance.clearMarks();
    performance.clearMeasures();
    performance.mark("configurator:start");
    window.__NEOVAA_GLTF_LOADS__ = 0;
    window.__NEOVAA_GLTF_FALLBACKS__ = 0;
    window.__NEOVAA_GLB_ERRORS__ = [];
    window.__NEOVAA_SCENE_READY__ = false;
    resetLodInstrumentation();
  };
  initTracker();

  const viewport = document.getElementById("viewport") as HTMLElement | null;
  const loadingEl = document.getElementById("viewport-loading");
  const fallbackEl = document.getElementById("viewport-fallback");
  if (!viewport) throw new Error("Viewport element missing");

  const theme = resolveTheme();
  // Host-theme adaptation: if the embed snippet passed the merchant's background /
  // text colour / font / button radius, override our tokens so we blend into their
  // store. No-op when standalone (no params).
  const hostTheme = getHostTheme();
  if (hostTheme) applyHostTheme(hostTheme);

  const profile = getDeviceProfile();
  trackEvent("session_start", {
    gpuTier: profile.gpuTier,
    isMobile: profile.isMobile,
    pixelRatio: profile.pixelRatio,
  });

  // Load the config FIRST, before the GPU/fallback decision, so a merchant-PAUSED product
  // vanishes on every device (a no-WebGL phone must get "unavailable", not the static cover).
  let product: ProductConfig;
  try {
    product = await loadProductConfig();
  } catch (e) {
    console.error("Failed to load product config", e);
    showFallback(fallbackEl, viewport, "Impossible de charger le produit");
    return;
  }

  // Merchant pause (kill-switch). The configurator is OFF: tell the host page to remove
  // the embed block entirely (it vanishes), and build nothing. Standalone shows a note.
  // Checked BEFORE the GPU fallback so a paused product always vanishes, GPU or not.
  if (product.paused) {
    try {
      if (window.top !== window.self) window.parent.postMessage({ type: "neovaa:paused" }, "*");
    } catch { /* cross-origin parent — the host listener still receives the "*" post above */ }
    showFallback(fallbackEl, viewport, "Temporarily unavailable");
    return;
  }

  // No usable WebGL on this device → static cover fallback (only reached when NOT paused).
  if (profile.fallbackToStatic) {
    showFallback(fallbackEl, viewport);
    return;
  }

  // Per-client MANUAL look override (config.theme) WINS over the auto-inherited host
  // theme, so a client can be fully branded regardless of their store's own CSS.
  if (product.theme) applyHostTheme(product.theme, "manual");

  // preload-warm (loading trick #5): the instant we know the product uses GLB
  // modules, kick off the Draco decoder download so it runs IN PARALLEL with the
  // heavy GLB transfer below — not serially after it. Free, no asset/quality
  // change, no-op for procedural-only products. Fire-and-forget.
  if (product.modules.some((m) => m.glb)) warmDracoDecoder();

  // 360 page only: the live 3D is hidden behind the spin (AR + dimensions only), so serve
  // the light .m.glb tier on EVERY device — kills the wasted 15–28 MB full-GLB download on
  // desktop (where AR is a QR the phone resolves itself). Zero visible/AR-quality impact.
  setForceMobileTier(true);

  // Cover freeze-frame (loading trick #2): the moment the config is known (a few
  // KB, ~instant), paint the product still INSIDE the viewer so the shopper sees
  // it immediately while the heavy GLB streams in behind. Faded out at
  // scene-ready (below) for an invisible swap. No-op if the product declares no
  // coverImage. Done before the engine/GLB work so it truly shows first.
  const coverEl = document.getElementById("viewport-cover") as HTMLImageElement | null;
  showCover(coverEl, product.viewer?.coverImage, theme, loadingEl);

  if (window.__NEOVAA_THEME__) {
    const root = document.documentElement;
    for (const [key, value] of Object.entries(window.__NEOVAA_THEME__)) {
      root.style.setProperty(`--${key.startsWith("nv-") ? key : `nv-${key}`}`, value);
    }
  }

  const decoded = decodeState(window.location.search, DEFAULT_STATE);
  // Per-product initial camera angle (e.g. Bungalow starts straight/front), only when
  // the URL didn't carry an explicit yaw/pitch.
  const _urlParams = new URLSearchParams(window.location.search);
  if (!_urlParams.has("y") && product.viewer?.initialYaw != null) decoded.yaw = product.viewer.initialYaw;
  if (!_urlParams.has("p") && product.viewer?.initialPitch != null) decoded.pitch = product.viewer.initialPitch;
  const initialState: ConfigState = validateState(decoded, product);

  const state = new Signal<ConfigState>(initialState);

  // Reveal gating: the low-FPS auto-degrade must NOT fire during the cold GLB decode
  // (a brief stutter would permanently drop pixelRatio + kill the contact shadow).
  // Only allow it a few seconds after the scene is actually revealed.
  let revealed = false;
  let revealedAt = 0;

  let engine: Engine;
  try {
    engine = new Engine({
      container: viewport,
      profile,
      initial: initialState,
      viewer: product.viewer,
      onCameraChange: ({ yaw, pitch, zoom }) => {
        state.set((prev) => ({ ...prev, yaw, pitch, zoom }));
      },
      onLowFps: (fps) => {
        // Ignore the cold-load stutter; only degrade once the scene has settled
        // (≥3s after reveal) so a brief decode dip doesn't stick at reduced quality.
        if (!revealed || performance.now() - revealedAt < 3000) return;
        console.warn(`Low FPS detected: ${fps.toFixed(1)}, degrading quality`);
        engine.degradeQuality();
      },
    });
  } catch (e) {
    console.error("Engine init failed", e);
    showFallback(fallbackEl, viewport, "3D engine not supported on this device");
    return;
  }

  // Dev-only capture hook (guarded by ?capture=1, never active in normal use):
  // exposes the engine so an offline turntable script can drive yaw precisely and
  // screenshot pixel-aligned product frames (used to regenerate the film frame set).
  if (location.search.includes("capture")) {
    (window as unknown as { __NEOVAA_ENGINE__?: Engine }).__NEOVAA_ENGINE__ = engine;
  }

  // AR controller (360 = BAKED): "View in your space" loads the PRE-BAKED USDZ/GLB for
  // the current slug (the 360's lighting frozen in), never a live USDZ export — that's
  // the validated process (brain: methodology/2026-06-20-ar-baked-usdz-mandatory). The
  // AR button hides for configs that aren't baked yet. Engine stays for dimensions only.
  // `spin.arMode: "live"` (Amada) : pas de bibliotheque de fichiers AR. Android recoit le GLB de
  // la config affichee (glbByForme, le meme que le 3D cache), iOS un USDZ fabrique sur le
  // telephone au tap depuis ce GLB, comme model-viewer dans le viewer classique.
  const arController = product.spin?.arMode === "live"
    ? createArController(engine, product)
    : createBakedArController(product, state);
  engine.onDestroy(() => arController.dispose());

  // THIS is the 360 configurator: always mount the pre-rendered 360 flipbook as the
  // primary viewer. It hides the live 3D behind it (kept only for AR + dimensions) and
  // swaps in the corner controls. Unlike the 3D configurator, there is no flag/gate —
  // the 360 is the whole point of this entry.
  void import("@/ui/Spin360").then((m) => m.initSpin360({ state, product, viewport, ar: arController, engine }));

  const useMobile = shouldUseMobileUI(profile.isMobile);
  // Match the opaque WebGL backdrop to the active mobile theme so the viewer
  // blends with the surrounding UI (desktop keeps the white studio set).
  // theme.viewerBg lets a brand give the 3D stage its OWN backdrop (Koala's
  // grey-green imagery vs their white rail); it also drives --nv-viewer-bg so
  // the stage card and the canvas are ONE surface, never two-tone.
  if (useMobile) {
    const stageBg = product.theme?.viewerBg ?? product.theme?.bg ?? hostTheme?.bg ?? THEME_VIEWER_BG[theme];
    // studioLighting products own a transparent canvas (StudioLighting sets scene.background=null);
    // don't repaint it opaque — let the host --nv-bg show so floor + surround read as one surface.
    if (!product.viewer?.studioLighting) engine.setBackground(stageBg);
    if (product.theme?.viewerBg) {
      for (const elT of [document.documentElement, document.body]) {
        elT?.style.setProperty("--nv-viewer-bg", product.theme.viewerBg);
      }
    }
  }

  // Tooling hook: generate the loading-cover still from the live engine itself (exact
  // recipe + camera framing → the freeze-frame is pixel/tone-identical to the first 3D
  // frame, no seam). Returns a transparent PNG data URL.
  if (typeof window !== "undefined") {
    (window as unknown as { __NEOVAA_CAPTURE_COVER__?: () => string | null }).__NEOVAA_CAPTURE_COVER__ =
      () => engine.captureCover();
  }

  // Mount the ENTIRE mobile page (header, hero, every config tile, CTA, reviews)
  // BEFORE the heavy GLB load — so the page paints instantly like a normal page,
  // and only the 3D viewer is left "loading" behind the cover freeze-frame. No
  // half-empty gap while the model streams in. Re-parenting #viewer into the
  // visible hero here also lifts it (and its cover) out of the FOUC-guarded
  // `.cfg { visibility:hidden }`, which is what makes the cover actually show.
  // The bootstrap backfills state (shape/size/material/colorway), so the single
  // initial build below already targets the correct config — no double build.
  // Interacting during the load is safe: rebuildAssembly is latest-wins (token).
  // Desktop builds its UI after the load (no cover-gap concept there).
  if (useMobile) bootstrapMobileUI({ product, state, engine, ar: arController });

  // Reveal (fade the cover + spinner, mark ready, start prefetch) the moment the
  // FIRST real assembly is actually on screen — driven by the engine, not by a
  // specific rebuild call. This stays correct if the user taps a shape/fabric
  // DURING the cold load: whichever build wins and displays first fires this, so
  // the cover never lifts on an empty viewer. One-shot via `revealed`.
  engine.onAssemblyShown(() => {
    if (revealed) return;
    revealed = true;
    revealedAt = performance.now();
    fadeOutLoading(loadingEl);
    // Mark revealed so a cover image that finishes loading AFTER this point does
    // not pop over the live model (the first-touch flash). Then fade any cover
    // that's already up.
    if (coverEl) coverEl.dataset.revealed = "1";
    fadeOutCover(coverEl);
    const ttfi = performance.now() - start;
    performance.mark("configurator:scene-ready");
    try {
      performance.measure("configurator:tti", "configurator:start", "configurator:scene-ready");
    } catch {
      /* mark missing — ignore */
    }
    window.__NEOVAA_SCENE_READY__ = true;
    window.__NEOVAA_ENGINE__ = engine;
    window.__NEOVAA_INSPECT__ = () => engine.inspectScene(state.get().formeId);
    window.__NEOVAA_CURRENT_PRODUCT__ = product;
    trackEvent("ready", { ttfi: Math.round(ttfi) });
    console.info(`[Configurator] Ready in ${ttfi.toFixed(0)}ms (tier=${profile.gpuTier})`);

    // NB: the 3D config's predictive prefetch (schedulePrefetch) is intentionally NOT
    // run here. On the 360 page the shown product is the pre-rendered spin, not the live
    // 3D, so warming the whole 3D catalog (~30 MB of proxies + fulls) is pure waste — the
    // exact bandwidth we're trying to keep off the 360 load.

    // If we arrived via a desktop AR QR scan (?neovaa_ar=auto), auto-open AR. The
    // controller already prefetches (Android) and generates the iOS USDZ from the
    // live model in the background — no per-shape file resolution here.
    arController.maybeAutoLaunch();
  });

  // Safety net: a build the engine refused to display because a declared GLB
  // failed (it would have been a white-box placeholder). No error message (Alex):
  //   - a model is already on screen (a SWITCH failed) → keep it silently; the
  //     selection rolls back (selectShape/selectSize) so the UI matches the view;
  //   - FIRST load failed but the cover is up → keep the cover silently (a real
  //     product still is a fine fallback);
  //   - FIRST load failed and there is NO cover at all → the only case we must
  //     surface, else the user stares at a dead spinner forever.
  engine.onAssemblyFailed(() => {
    const coverUp = !!coverEl && coverEl.classList.contains("is-shown");
    // Produit 360-first (`product.spin`) : le flipbook est le visuel, le 3D cache ne sert
    // qu a l AR et aux cotes. Un GLB absent ne doit jamais masquer le 360 (Alex 2026-09-07).
    if (product.spin) {
      // Le 360 est deja monte : on retire juste le spinner et la cover, sans message.
      if (!revealed) { fadeOutLoading(loadingEl); fadeOutCover(coverEl); }
      return;
    }
    if (!revealed && !coverUp) {
      showFallback(fallbackEl, viewport, "Couldn't load the 3D model. Please retry.");
    }
  });

  try {
    await engine.rebuildAssembly(product, state.get());
  } catch (e) {
    console.error("Assembly build failed", e);
    showFallback(fallbackEl, viewport, "Failed to load the model");
    engine.destroy();
    return;
  }

  if (!useMobile) {
    bootstrapUI({
      product,
      state,
      engine,
      isMobile: profile.isMobile,
    });
  }
}

function showFallback(fallbackEl: HTMLElement | null, viewport: HTMLElement, message?: string): void {
  if (fallbackEl) {
    if (message) fallbackEl.innerHTML = `<p>${message}</p>`;
    fallbackEl.classList.remove("hidden");
  }
  viewport.classList.add("hidden");
  document.getElementById("viewport-loading")?.classList.add("hidden");
  // Drop the cover too — otherwise the freeze-frame would sit on top of the
  // fallback message (it lives inside #viewer, above the fallback in z-order).
  document.getElementById("viewport-cover")?.classList.add("hidden");
}

function fadeOutLoading(el: HTMLElement | null): void {
  if (!el) return;
  el.classList.add("fade-out");
  setTimeout(() => el.classList.add("hidden"), 320);
}

/** Show the cover freeze-frame. `url` may carry a `{theme}` token (one still per
 *  theme background). No-op when no coverImage is declared or the element is
 *  missing. A load error just leaves it hidden (the spinner still covers the
 *  wait), never blocks boot. */
function showCover(
  el: HTMLImageElement | null,
  url: string | undefined,
  theme: string,
  loadingEl: HTMLElement | null,
): void {
  if (!el || !url) return;
  const resolved = url.replace(/\{theme\}/g, theme);
  el.addEventListener("load", () => {
    // Race guard: on a fast GPU the 3D can be revealed BEFORE the cover image
    // finishes loading. Without this, the cover would pop up on top of the live
    // model (a flash the moment you grab/rotate the sofa, since fadeOutCover
    // already ran before the cover existed). If revealed, just stay hidden.
    if (el.dataset.revealed === "1") { el.classList.add("hidden"); return; }
    el.classList.add("is-shown");
    // Cover is up → it IS the loading state. Drop the spinner so there's no
    // "loading signal" on top of the product still (Alex's requirement).
    loadingEl?.classList.add("hidden");
  }, { once: true });
  el.addEventListener("error", () => {
    // Keep the spinner as the fallback wait indicator; never block boot.
    console.warn(`[Cover] failed to load ${resolved} — keeping spinner`);
  }, { once: true });
  el.src = resolved;
}

/** Fade the cover out once the real 3D is ready, then remove it from the layout.
 *  The fade reads as the model "coming alive" since the still is pixel-aligned
 *  with the first frame. */
function fadeOutCover(el: HTMLImageElement | null): void {
  if (!el || !el.src) return;
  el.classList.remove("is-shown");
  el.classList.add("fade-out");
  setTimeout(() => {
    el.classList.add("hidden");
    el.src = "";
  }, 380);
}

main().catch((e) => {
  console.error("[Configurator] Bootstrap failed", e);
  const fallbackEl = document.getElementById("viewport-fallback");
  const viewport = document.getElementById("viewport");
  if (fallbackEl && viewport) {
    showFallback(fallbackEl, viewport, "Une erreur est survenue au chargement du configurateur.");
  }
});
